<template>
  <v-tooltip v-if="tooltip" bottom>
    <template v-slot:activator="{ on, attrs }">
      <button
        ref="baseButton"
        :class="[baseType, baseSize, baseIcon, color, baseRadius, baseFontSize]"
        :data-intro="dataIntro"
        :style="{ 'min-width': loading ? `${baseButtonWidth}` : '' }"
        class="base-button"
        v-bind="attrs"
        v-on="{ ...on, ...$listeners }"
      >
        <v-progress-circular
          v-if="loading"
          :size="16"
          width="3"
          color="gray"
          indeterminate
        />
        <span v-else :class="[icon, icon && 'mdi']">
          <slot />
        </span>
      </button>
    </template>
    <span>{{ tooltip }}</span>
  </v-tooltip>

  <button
    v-else
    ref="baseButton"
    :class="[baseType, baseSize, baseIcon, color, baseRadius, baseFontSize]"
    :data-intro="dataIntro"
    :style="{ 'min-width': loading ? `${baseButtonWidth}` : '' }"
    class="base-button"
    v-bind="$attrs"
    v-on="$listeners"
  >
    <v-progress-circular
      v-if="loading"
      :size="16"
      width="3"
      color="gray"
      indeterminate
    />
    <template v-else>
      <span :class="[icon, icon && 'mdi']"></span>
      <span>
        <slot />
      </span>
    </template>
  </button>
</template>

<script>
const types = ['primary', 'secondary', 'tertiary', 'danger', 'neutral', 'trial']
const sizes = ['default', 'small', 'xsmall']

export default {
  name: 'BaseBtn',
  data: () => ({
    baseButtonWidth: '',
  }),
  props: {
    type: {
      type: String,
      default: 'primary',
      validator: (value) => types.includes(value),
      required: false,
    },
    size: {
      type: String,
      default: 'default',
      validator: (value) => sizes.includes(value),
      required: false,
    },
    fontSize: {
      type: String,
      default: '',
      required: false,
    },
    color: {
      type: String,
      default: '',
      required: false,
    },
    icon: {
      type: String,
      default: '',
      required: false,
    },
    tooltip: {
      type: String,
      default: '',
      required: false,
    },
    dataIntro: {
      type: String,
      default: null,
      required: false,
    },
    noBorderRadius: {
      type: String,
      default: null,
      required: false,
    },
    loading: {
      type: Boolean,
      default: false,
      required: false,
    },
    keepWidthOnLoading: {
      type: Boolean,
      default: false,
      required: false,
    },
  },
  watch: {
    loading: {
      handler(val) {
        if (val) {
          this.handleBtnWidthOnLoading()
        }
      },
      immediate: true,
    },
  },
  computed: {
    baseType() {
      return this.baseClass(this.type)
    },
    baseSize() {
      return this.baseClass(this.size)
    },
    baseIcon() {
      if (this.icon) return `${this.baseClass('icon')} d-flex align-center`
      return ''
    },
    baseRadius() {
      if (this.noBorderRadius) return this.borderClass(this.noBorderRadius)
      return ''
    },
    baseFontSize() {
      if (this.fontSize) return this.fontSizeClass(this.fontSize)
      return ''
    },
  },

  methods: {
    baseClass(item) {
      return `base-button--${item}`
    },
    borderClass(item) {
      const classes = {
        right: this.baseClass('no-right-radius'),
        left: this.baseClass('no-left-radius'),
        both: [
          this.baseClass('no-left-radius'),
          this.baseClass('no-right-radius'),
        ],
      }
      return classes[item]
    },
    fontSizeClass(item) {
      return `font-size--${item}`
    },
    handleBtnWidthOnLoading() {
      if (this.keepWidthOnLoading && this.$refs?.baseButton) {
        this.baseButtonWidth =
          this.$refs?.baseButton?.getBoundingClientRect().width + 'px'
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.base-button {
  display: inline-block;
  border: 1px solid var(--v-brand-base);
  border-radius: $border-radius;

  font: {
    family: Rubik;
    size: 0.875rem;
    weight: 500;
  }
  text: {
    align: center;
    transform: initial;
  }
  line-height: 150%;
  letter-spacing: 0px;

  i {
    size: 0.875rem;
    color: inherit !important;
  }

  cursor: pointer;

  // types
  &--primary {
    background-color: var(--brand-60-base-light);

    color: var(--neutral-100-white-light);

    &:hover {
      background-color: var(--brand-40-dark-light);
      border-color: var(--brand-40-dark-light);
    }

    &:active {
      background-color: var(--brand-30-darker-light);
      border-color: var(--brand-30-darker-light);
    }

    // disabled
    &:disabled {
      background-color: var(--neutral-90-light);
      border-color: var(--neutral-90-light);

      color: var(--neutral-70-light);
      cursor: not-allowed;
    }
  }
  &--secondary {
    border-color: varFigma(brand-60-base);
    color: varFigma(brand-60-base);

    &:hover {
      background-color: varFigma(brand-99-lightest);
      border-color: varFigma(brand-50);

      color: varFigma(brand-50);
    }

    &:active {
      background-color: varFigma(brand-95-lighter);
      border-color: varFigma(brand-50);

      color: varFigma(brand-50);
    }

    // disabled
    &:disabled {
      background-color: varFigma(neutral-100-white);
      border-color: varFigma(neutral-70);
      color: varFigma(neutral-70);

      cursor: not-allowed;
    }
  }
  &--tertiary {
    background-color: varFigma(neutral-95-lightest);
    border-color: varFigma(neutral-95-lightest);

    color: varFigma(neutral-30-base);

    &:hover {
      background-color: varFigma(neutral-90);
      border-color: varFigma(neutral-90);

      color: varFigma(neutral-30-base);
    }

    &:active {
      background-color: varFigma(neutral-80-lighter);
      border-color: varFigma(neutral-80-lighter);

      color: varFigma(neutral-30-base);
    }

    // disabled
    &:disabled {
      background-color: varFigma(neutral-100-white);
      border-color: varFigma(neutral-100-white);
      color: varFigma(neutral-70);

      cursor: not-allowed;
    }
  }

  &--danger {
    background-color: varFigma(feedback-danger-background);
    border-color: varFigma(feedback-danger-background);

    color: varFigma(feedback-danger);

    &:hover {
      border-color: varFigma(feedback-danger);
    }

    &:active {
      border-color: varFigma(feedback-danger-dark);
    }

    // disabled
    &:disabled {
      background-color: varFigma(neutral-100-white);
      border-color: varFigma(neutral-100-white);
      color: varFigma(neutral-70);

      cursor: not-allowed;
    }
  }

  &--neutral {
    background-color: varFigma(neutral-100-white);
    border-color: varFigma(neutral-100-white);
    color: varFigma(neutral-30-base);

    &:hover {
      background-color: varFigma(neutral-95-lightest);
      border-color: varFigma(neutral-95-lightest);
    }

    &:active {
      background-color: varFigma(neutral-90);
      border-color: varFigma(neutral-90);
    }

    // disabled
    &:disabled {
      background-color: varFigma(neutral-100-white);
      border-color: varFigma(neutral-100-white);
      color: varFigma(neutral-70);

      cursor: not-allowed;
    }
  }

  &--trial {
    background-color: varFigma(trial-trial);
    border-color: varFigma(trial-trial);
    color: varFigma(neutral-100-white);

    &:hover {
      background-color: varFigma(trial-trial-dark);
      border-color: varFigma(trial-trial-dark);
    }

    &:active {
      background-color: varFigma(trial-trial-darkest);
      border-color: varFigma(trial-trial-darkest);
    }
  }

  // sizes
  &--default {
    height: 40px;
    padding: 0 $spacing-base;

    &[class*='icon'] {
      padding: $spacing-minus-1;
    }
  }
  &--small {
    height: 32px;
    padding: 0 $spacing-minus-1;
    font-size: 0.875rem;

    &[class*='icon'] {
      padding: $spacing-minus-2;

      .mdi {
        font-size: 0.875rem;
      }
    }
  }
  &--xsmall {
    height: 24px;
    padding: 0 $spacing-minus-2;
    font-size: 0.75rem;

    &[class*='icon'] {
      padding: $spacing-minus-3;

      .mdi {
        font-size: 0.75rem;
      }
    }
  }

  &--no-right-radius {
    border-top-right-radius: 0px;
    border-bottom-right-radius: 0px;
  }

  &--no-left-radius {
    border-top-left-radius: 0px;
    border-bottom-left-radius: 0px;
  }

  // icon
  .mdi {
    line-height: 1rem;
  }
}

.bg-dark {
  .base-button {
    // types
    &--primary:not(.bg-dark--ignore) {
      // disabled
      &:disabled {
        background-color: varFigma(neutral-90);
        border-color: varFigma(neutral-90);
        color: varFigma(neutral-70);

        cursor: not-allowed;
      }
    }
  }
}

.font-size {
  &--default {
    font-size: 1rem;
  }

  &--small {
    font-size: 0.875rem;
  }

  &--xsmall {
    font-size: 0.75rem;
  }
}
</style>
